import { useMemo } from "react";
import { Building2Icon } from "lucide-react";

import { Row, Text } from "@/components";

import { useUserSheet } from "@/components/user-sheet/userSheetContext";
import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import {
  companyMatchTone,
  useCustomerCompanyMatches,
} from "@/hooks/useCustomerCompanyMatches";
import { COMPANY_MATCH_REASONS, companies } from "@/lib/companies";
import { cn } from "@/lib/utils";
import { WorkExperienceType } from "@/types";

export const WorkedAtCustomerBanner = ({
  className,
  isCurrentLaborupPlacement,
}: {
  className?: string;
  isCurrentLaborupPlacement?: boolean;
}) => {
  /* Database CRUD Operations */
  // Read
  const { jobId, workerId } = useUserSheet();
  const userApi = useGetCandidacySheetData({ jobId, userId: workerId });

  /* React Hooks */
  const { findMatches } = useCustomerCompanyMatches();

  const matchedCompany = useMemo(() => {
    const workExperiences: WorkExperienceType[] | undefined =
      userApi.data?.workExperience;
    if (!workExperiences) return undefined;

    // Show the highest ranked match
    return findMatches(companies.extractEmployerNames(workExperiences))?.[0];
  }, [findMatches, userApi.data?.workExperience]);

  if (!matchedCompany) return null;

  /* Helpers */
  // "MCM", or "MCM Fabrication (related to MCM)" when the hit came through one
  // of the customer's related company names rather than its own.
  const describeCompany = () =>
    matchedCompany.matched === matchedCompany.company
      ? matchedCompany.company
      : `${matchedCompany.matched} (related to ${matchedCompany.company})`;

  const getMessage = () => {
    if (
      matchedCompany.reason === COMPANY_MATCH_REASONS.SAME_COMPANY ||
      matchedCompany.reason === COMPANY_MATCH_REASONS.CURRENT_CUSTOMER
    ) {
      return `${matchedCompany.reason}: ${describeCompany()}`;
    }
    if (isCurrentLaborupPlacement) {
      return `${COMPANY_MATCH_REASONS.CURRENT_PLACEMENT}: ${describeCompany()}`;
    }
    return `${COMPANY_MATCH_REASONS.OTHER}: ${describeCompany()}`;
  };

  /* Render */
  const render = () => {
    const tone = companyMatchTone(
      matchedCompany.reason,
      isCurrentLaborupPlacement,
    );
    return (
      <Row
        className={cn(
          "min-h-10 w-full items-center gap-2 border-b px-6 py-4",
          tone.container,
          className,
        )}
      >
        <Building2Icon className={cn("h-4 w-4 shrink-0", tone.content)} />
        <Text className={cn("text-sm font-medium", tone.content)}>
          {getMessage()}
        </Text>
      </Row>
    );
  };

  return render();
};
